import router from "../../router/index.js";

/**
 * State
 */
const state = {
    id: null,
    activeMakeId: null,
    activeModel: null,
    activeModelId: null,
    activeModelVersionId: null,
    activeTypeId: null,
    askBackGlass: false,
    askFrame: false,
    hasInsurance: null,
    imei: null,
    makes: [],
    models: [],
    modelVersions: [],
    problems: [],
    reference: null,
    selectedProblems: [],
    selectedProblemsPricing: [],
    types: [],
    upsellOptions: {},
    triageQuestions: {},
    triageAnswers: {},
    applianceModelCode: null
};

/**
 * Getters
 */
const getters = {
    /** Get the active model version id */
    activeMakeId: (state) => state.activeMakeId,

    /** Get the active model version id */
    activeModelVersionId: (state) => state.activeModelVersionId,

    /** Checks to see if a problem has been selected and returns false if not */
    aProblemHasBeenSelected: (state) => state.selectedProblems.length > 0,

    /** Ask the customer if they need the back glass repaired */
    askBackGlass: (state) => state.askBackGlass,

    /** Ask the customer if they need the frame repaired */
    askFrame: (state) => state.askFrame,

    /** Returns the back glass problem for checks */
    backGlassProblem: state => {
        if (state.problems.length > 0) {
            for (let i = 0; i < state.problems.length; i++) {
                if (state.problems[i] && state.problems[i].code === "back-glass-replacement") {
                    return state.problems[i];
                }
            }
        }
        return null;
    },

    /** Returns the battery problem for checks */
    batteryProblem: state => {
        if (state.problems.length > 0) {
            for (let i = 0; i < state.problems.length; i++) {
                if (state.problems[i] && state.problems[i].code === "battery-replacement") {
                    return state.problems[i];
                }
            }
        }
        return null;
    },

    /** Returns a bool of if the battery problem has been selected or not */
    batteryIsSelected: (state, getters) => {
        if (getters.batteryProblem && state.selectedProblems.length > 0) {
            for (let i = 0; i < state.selectedProblems.length; i++) {
                if (state.selectedProblems[i] && state.selectedProblems[i].id === getters.batteryProblem.id) {
                    return true;
                }
            }
        }
        return false;
    },

    /** Gets the current device reference */
    currentDeviceId: (state) => state.id,

    /** Returns a bool of if we should take the customer through the extras page */
    displayUpsellOptions: (getters) => {
        if (!getters.upsellOptions || Object.keys(getters.upsellOptions).length === 0) return false;

        let problemCodes = Object.keys(getters.upsellOptions);

        for (let i = 0; i < problemCodes.length; i++) {

            let optionAlreadySelected = getters.selectedProblems.filter(problem => problem.code === problemCodes[i]);

            if (!optionAlreadySelected.length) return true;
        }

        return false;
    },

    /** Returns the frame problem for checks */
    frameProblem: state => {
        if (state.problems.length > 0) {
            for (let i = 0; i < state.problems.length; i++) {
                if (state.problems[i] && state.problems[i].code === "frame-replacement") {
                    return state.problems[i];
                }
            }
        }
        return null;
    },

    /** The selected insurance status */
    hasInsuranceStatus: (state) => state.hasInsurance,

    /** Returns true if the passed through problem is the one set in the state */
    hasProblem: state => problem => {
        if (state.selectedProblems.length > 0) {
            for (let i = 0; i < state.selectedProblems.length; i++) {
                if (state.selectedProblems[i] && state.selectedProblems[i].id === problem.id) {
                    return true;
                }
            }
        }
        return false;
    },

    /** Returns the IMEI */
    imei: state => state.imei,

    /** Returns a bool of if the selected make is Samsung */
    makeIsSamsung: state => {
        for (let i = 0; i < state.makes.length; i++) {
            if (state.makes[i].id === state.activeMakeId) {
                return state.makes[i].code === "samsung";
            }
        }
        return false;
    },

    /** Get the makes */
    makes: (state) => state.makes,

    /** Check if a make has been selected */
    makeHasBeenSelected: (state) => state.activeMakeId !== null,

    /** Returns true if the passed through make is the one set in the state */
    makeIsActive: (state) => make => state.activeMakeId === make.id,

    /** Get the models */
    models: (state) => state.models,

    /** Check if a model has been selected */
    modelHasBeenSelected: (state) => state.activeModelId !== null,

    /** Returns true if the passed through model is the one set in the state */
    modelIsActive: (state) => model => state.activeModelId === model.id,

    /** Get the model versions */
    modelVersions: (state) => state.modelVersions,

    /** Check if a model version has been selected */
    modelVersionHasBeenSelected: (state) => state.activeModelVersionId !== null,

    /** Returns true if the passed through model version is the one set in the state */
    modelVersionIsActive: (state) => modelVersion => state.activeModelVersionId === modelVersion.id,

    /** Get the available problems */
    problems: (state) => state.problems,

    /** Get the selected make id */
    selectedMakeId: (state) => state.activeMakeId,

    /** Get the selected model */
    selectedModel: (state) => state.activeModel,

    /** Get the selected problems */
    selectedProblems: (state) => state.selectedProblems,

    /** Get the selected problems with pricing */
    selectedProblemsPricing: (state) => state.selectedProblemsPricing,

    /** Get number selected problems */
    totalSelectedProblems: (state) => state.selectedProblems.length,

    /** Get the types */
    types: (state) => state.types,

    /** Check if a type has been selected */
    typeHasBeenSelected: (state) => state.activeTypeId !== null,

    /** Returns true if the passed through type is the one set in the state */
    typeIsActive: (state) => type => state.activeTypeId === type.id,

    /** Get the upsell options available on this device */
    upsellOptions: (state) => state.upsellOptions,

    /** Get the triage questions that relate to the selected white goods model **/
    triageQuestions: (state) => state.triageQuestions,

    /** Get the triage questions that relate to the selected white goods model **/
    triageAnswers: (state) => state.triageAnswers,

    /** Get the white goods model code for this device **/
    applianceModelCode: (state) => state.applianceModelCode
};

/**
 * Actions
 */
const actions = {
    /** Checks that we have the correct details for a warranty repair and returns the user to the correct page if not **/
    checkDeviceWarrantyDetails({state, getters}) {
        return new Promise((resolve) => {
            if (getters.inWarrantyStatus && (!state.imei || !getters.purchaseDate)) {
                router.push({name: 'deviceWarrantyStatus'});
            }
            resolve();
        });
    },

    /** Clear this device */
    clearDevice({commit}) {
        return new Promise((resolve) => {
            commit('CLEAR_DEVICE');
            resolve();
        });
    },

    /** Load makes */
    loadMakes({rootGetters, commit}) {
        return new Promise((resolve, reject) => {
            let repairTypeId = 1;
            if (rootGetters.repairType === 'appliance') {
                repairTypeId = 2;
            }

            this.$axios.get(`/api/v4/makes/list/type/${repairTypeId}?show_available=true`)
                .then(response => {
                    commit('SET_MAKES', response.data.data);
                    resolve();
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /** Load makes */
    loadTriageQuestions({commit, getters}) {
        return new Promise((resolve, reject) => {
            this.$axios.post(`/api/v4/booking/${getters.reference}/triage-questions`, {
                type: getters.selectedModel.type_id
            })
            .then(response => {
                commit('SET_TRIAGE_QUESTIONS', response.data.data);
                resolve();
            })
            .catch(error => {
                reject(error);
            });
        });
    },

    /** Load model types */
    loadTypes({rootGetters, commit}) {
        return new Promise((resolve, reject) => {
            let repairTypeId = 1;
            if (rootGetters.repairType === 'appliance') {
                repairTypeId = 2;
            }

            this.$axios.get(`/api/v4/device/types?repair_type_id=${repairTypeId}`)
                .then(response => {
                    commit('SET_TYPES', response.data.data);
                    resolve();
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /** Load models */
    loadModels({state, commit}) {
        return new Promise((resolve, reject) => {
            this.$axios.get(`/api/v4/models?make_id=${state.activeMakeId}&type_id=${state.activeTypeId}&order_by=priority,asc&filter_relationships=true`)
                .then(response => {
                    commit('SET_MODELS', response.data.data);
                    resolve();
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /** Load model versions */
    loadModelVersions({state, commit}) {
        return new Promise((resolve, reject) => {
            this.$axios.get(`/api/v4/models/${state.activeModelId}/versions`)
                .then(response => {
                    commit('SET_MODEL_VERSIONS', response.data.data);
                    resolve();
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /** Load Problems */
    loadProblems({state, rootGetters, commit}) {
        return new Promise((resolve, reject) => {
            this.$axios.get(`/api/v4/models/versions/${state.activeModelVersionId}?include_warranty_repairs=${rootGetters.inWarrantyStatus}`)
                .then(response => {
                    commit('SET_PROBLEMS', response.data.data.problems);
                    resolve();
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    /** Sets the insurance status */
    selectInsuranceState({commit}, insuranceState) {
        commit('SET_INSURANCE_STATE', insuranceState);
    },

    /** Make selection */
    selectMake({commit}, makeSelected) {
        commit('CLEAR_ACTIVE_TYPE');
        commit('SET_ACTIVE_MAKE', makeSelected.id);
        commit('SET_TYPES', makeSelected.types);
    },

    /** Model selection */
    selectModel({commit}, modelSelected) {
        commit('CLEAR_ACTIVE_MODEL_VERSION');
        commit('CLEAR_PROBLEMS');
        commit('SET_ACTIVE_MODEL', modelSelected);
        // dispatch('loadModelVersions'); // This was removed so the "loadModelVersions" function can be manually run on the select colour page
    },

    /** Model version selection */
    selectModelVersion({commit, dispatch}, modelVersionIdSelected) {
        commit('CLEAR_WARRANTY_STATE');
        commit('CLEAR_INSURANCE_STATE');
        commit('SET_ACTIVE_MODEL_VERSION', modelVersionIdSelected);
        dispatch('loadProblems');
    },

    /** Problem selection */
    toggleProblem({commit, dispatch, getters}, selectedProblem) {
        return new Promise((resolve, reject) => {
            let problem = Object.assign({}, selectedProblem);
            problem.symptoms = [];

            if (getters.hasProblem(problem)) {
                commit('REMOVE_SELECTED_PROBLEM', problem);

            } else {
                commit('ADD_SELECTED_PROBLEM', problem);
            }

            if (problem.code === "screen-replacement" && getters.batteryIsSelected) {
                if (getters.selectedModel && getters.selectedModel.battery_required_for_screen_repair) {
                    commit('REMOVE_SELECTED_PROBLEM', getters.batteryProblem);
                }
            }

            dispatch('updateDevice')
                .then(() => {
                    resolve();
                }).catch(() => {
                    reject();
                });
        });
    },

    /** Symptom selection */
    toggleSymptom({commit, dispatch, getters}, {problem: problem, symptom: symptom}) {
        return new Promise((resolve, reject) => {

            let existingProblem = null;

            getters.selectedProblems.forEach((selectedProblem) => {
                if (selectedProblem.id === problem.id) {
                    existingProblem = selectedProblem;
                }
            });

            if (!existingProblem) return reject();

            if (!existingProblem.symptoms.length) {
                existingProblem.symptoms.push(symptom);

            } else {
                let existingSymptomFound = false;

                existingProblem.symptoms.forEach((existingSymptom, key) => {
                    if (existingSymptom.id === symptom.id) {
                        existingProblem.symptoms.splice(key, 1);
                        existingSymptomFound = true;
                    }
                });

                if (!existingSymptomFound) {
                    existingProblem.symptoms.push(symptom);
                }
            }

            commit('UPDATE_SELECTED_PROBLEM', existingProblem);

            dispatch('updateDevice')
                .then(() => {
                    resolve();
                }).catch(() => {
                    reject();
                });
        });
    },

    /** Type selection */
    selectType({commit, dispatch}, typeSelected) {
        commit('CLEAR_ACTIVE_MODEL');
        commit('SET_ACTIVE_TYPE', typeSelected.id);
        dispatch('loadModels');
    },

    /** Sets the device data */
    setDeviceData({commit}, deviceData) {
        return new Promise((resolve) => {
            commit('SET_DEVICE_DATA', deviceData);
            commit('SET_SELECTED_PROBLEMS', deviceData);
            resolve();
        });
    },

    /** Retrieve the data from the store using the supplied device key */
    retrieveDevice({commit, dispatch, getters}, {deviceKey: deviceKey, setModelVersionId: setModelVersionId}) {
        return new Promise((resolve, reject) => {
            this.$axios.get(`/api/v4/wefix-booking/${getters.reference}/device/${deviceKey}`)
                .then(response => {
                    commit('SET_BOOKING_FROM_RESPONSE', response.data.data);

                    const bookingDevice = response.data.data.booking_device;

                    commit('SET_DEVICE_DATA', bookingDevice);
                    commit('SET_SELECTED_PROBLEMS', bookingDevice);

                    dispatch('loadProblems');
                    dispatch('loadModelVersions');
                    dispatch('loadModels');
                    dispatch('loadMakes');
                    dispatch('loadTriageQuestions');

                    if (!setModelVersionId) {
                        commit('SET_ACTIVE_MODEL_VERSION', null);
                    }

                    resolve();

                }).catch(error => {
                    reject(error);
                })
        });
    },

    /** Update this device */
    updateDevice({commit, getters, state, dispatch}, updateProblems = false) {
        return new Promise((resolve, reject) => {
            if (state.id && !updateProblems) {
                resolve();
            }

            if(getters.carrier === 'appliance') {
                dispatch('loadTriageQuestions');
            }

            this.$axios.post(`/api/v4/wefix-booking/${getters.reference}/device/${state.id}`, {
                model_version_id: state.activeModelVersionId,
                problems: state.selectedProblems,
                reference: state.reference,
                imei: state.imei,
                device_type_id: getters.selectedModel.type_id,
                triage_answers: state.triageAnswers,
                appliance_model_code: state.applianceModelCode
            }).then(response => {
                commit('SET_BOOKING_FROM_RESPONSE', response.data.data);

                const bookingDevice = response.data.data.booking_device;

                commit('SET_DEVICE_DATA', bookingDevice);

                if (updateProblems) {
                    commit('SET_SELECTED_PROBLEMS', bookingDevice);
                }

                resolve();

            }).catch(error => {
                reject(error);
            })
        });
    },

    /** Update the model code for white goods type bookings **/
    updateApplianceModelCode({commit}, applianceModelCode) {
        return new Promise((resolve) => {
            commit('SET_APPLIANCE_MODEL_CODE', applianceModelCode);
            resolve();
        });
    },

    /** Store the device IMEI */
    storeDeviceImei(__, {deviceId, email, imei}) {
        return new Promise((resolve, reject) => {
            this.$axios.post(`/api/v4/wefix-booking/devices/${deviceId}/imei`, {
                imei: imei,
                email: email,
                customer_update: true
            }).then(() => {
                resolve();
            })
            .catch(errors => {
                reject(errors);
            });
        });
    },

    /** Delete the supplied device */
    deleteDevice({commit, getters, state}, deviceId) {
        return new Promise((resolve, reject) => {
            this.$axios.delete(`/api/v4/wefix-booking/${getters.reference}/device/${deviceId}`)
                .then(response => {
                    commit('SET_BOOKING_FROM_RESPONSE', response.data.data);

                    if (deviceId === state.id) {
                        commit('CLEAR_DEVICE');
                    }

                    resolve();
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    setTriageAnswers({commit}, triageAnswers) {
        return new Promise((resolve) => {
            commit('SET_TRIAGE_ANSWERS', triageAnswers);
            resolve();
        });
    }
};

/**
 * Mutations
 */
const mutations = {
    /** Add a problem to the collection of selected problems **/
    ADD_SELECTED_PROBLEM(state, problem) {
        state.selectedProblems.push(problem);
    },

    /** Clear the active model id and models */
    CLEAR_ACTIVE_MODEL(state) {
        state.activeModelId = null;
        state.models = [];
    },

    /** Clear the active model version id & model versions */
    CLEAR_ACTIVE_MODEL_VERSION(state) {
        state.activeModelVersionId = null;
        state.modelVersions = [];
    },

    /** Clear the active type id */
    CLEAR_ACTIVE_TYPE(state) {
        state.activeTypeId = null;
    },

    /** Clears the device state back to default */
    CLEAR_DEVICE(state) {
        state.id = null;
        state.activeMakeId = null;
        state.activeModel = null;
        state.activeModelId = null;
        state.activeModelVersionId = null;
        state.activeTypeId = null;
        state.askBackGlass = false;
        state.askFrame = false;
        state.hasInsurance = null;
        state.imei = null;
        state.makes = [];
        state.models = [];
        state.modelVersions = [];
        state.problems = [];
        state.reference = null;
        state.selectedProblems = [];
        state.selectedProblemsPricing = [];
        state.types = [];
        state.upsellOptions = {};
        state.triageQuestions = {};
        state.triageAnswers = {};
        state.applianceModelCode = null;
    },

    /** Clear the insurance state */
    CLEAR_INSURANCE_STATE(state) {
        state.hasInsurance = null;
    },

    /** Clear the problems and selected problems */
    CLEAR_PROBLEMS(state) {
        state.selectedProblems = [];
        state.problems = [];
    },

    /** Remove a problem from the collection of selected problems **/
    REMOVE_SELECTED_PROBLEM(state, problem) {
        state.selectedProblems.forEach((selectedProblem, key) => {
            if (selectedProblem.id === problem.id) {
                state.selectedProblems.splice(key, 1);
            }
        });
    },

    /** Set active make id */
    SET_ACTIVE_MAKE(state, makeId) {
        state.activeMakeId = makeId;
    },

    /** Set active model & id */
    SET_ACTIVE_MODEL(state, model) {
        state.activeModel = model;
        state.activeModelId = model.id;
    },

    /** Set active model version id */
    SET_ACTIVE_MODEL_VERSION(state, modelVersionId) {
        state.activeModelVersionId = modelVersionId;
    },

    /** Set active type id */
    SET_ACTIVE_TYPE(state, typeId) {
        state.activeTypeId = typeId;
    },

    /**  */
    SET_DEVICE_DATA(state, bookingDevice) {
        state.id = bookingDevice.reference;
        state.activeMakeId = bookingDevice.model_version.model.make_id;
        state.activeModel = bookingDevice.model_version.model;
        state.activeModelId = bookingDevice.model_version.model_id;
        state.activeModelVersionId = bookingDevice.device.model_version_id;
        state.activeTypeId = bookingDevice.model_version.model.type_id;
        state.askBackGlass = bookingDevice.ask_back_glass;
        state.askFrame = bookingDevice.ask_frame;
        state.imei = bookingDevice.device.imei;
        state.reference = bookingDevice.device.customer_reference;
        state.upsellOptions = bookingDevice.upsell_options;
        state.triageAnswers = bookingDevice.triage_answers;
        state.applianceModelCode = bookingDevice.device.full_model_code;
    },

    /** Set the selected problems and problem pricing */
    SET_SELECTED_PROBLEMS(state, bookingDevice) {
        state.selectedProblems = bookingDevice.selected_problems;
        state.selectedProblemsPricing = bookingDevice.selected_problems_pricing;
    },

    /** Set the insurance state */
    SET_INSURANCE_STATE(state, insuranceState) {
        state.hasInsurance = insuranceState;
    },

    /** Set the makes */
    SET_MAKES(state, makes) {
        state.makes = makes;
    },

    /** Set the triage questions */
    SET_TRIAGE_QUESTIONS(state, questions) {
        state.triageQuestions = questions;
    },

    /** Set the triage answers */
    SET_TRIAGE_ANSWERS(state, answers) {
        state.triageAnswers = answers;
    },

    /** Set the models */
    SET_MODELS(state, models) {
        state.models = models;
    },

    /** Set the models */
    SET_MODEL_VERSIONS(state, modelVersions) {
        state.modelVersions = modelVersions;
    },

    /** Set the problems */
    SET_PROBLEMS(state, problems) {
        state.problems = problems;
    },

    /** Set the types */
    SET_TYPES(state, types) {
        state.types = types;
    },

    /** Set the white goods model code */
    SET_APPLIANCE_MODEL_CODE(state, applianceModelCode) {
        state.applianceModelCode = applianceModelCode;
    },

    /** Removes and then adds back the selected problem */
    UPDATE_SELECTED_PROBLEM(state, problem) {
        state.selectedProblems.forEach((selectedProblem, key) => {
            if (selectedProblem.id === problem.id) {
                state.selectedProblems.splice(key, 1);
            }
        });
        state.selectedProblems.push(problem);
    }
};

export default {
    state,
    getters,
    actions,
    mutations
}